import { useEffect, useRef, useState } from "react";
import Button from "../../../components/shared/buttons/button";
import DateSelector from "../../../components/shared/fields/DateSelector";
import GridCard from "../../../components/shared/gridCard/GridCard";
import Box from "../../../images/boxes.svg";
import KgGreen from "../../../images/kg_green.svg";
import KgRejected from "../../../images/kg_rejected.svg";
import Datatable from "../../../components/shared/datatable/Datatable";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import AddEmployeeModal from "./AddEmployeeModal";
import {
  useGetReworkEmployeeDataQuery,
  useGetReworkLogsQuery,
  useGetReworkAreaSummaryQuery,
} from "./reworkApi";
import EditReworkLogs from "./EditReworkLogs";
import Humidity from "./Humidity";
import { useNavigate } from "react-router";
import useTranslator from "../../../hooks/useTranslator";
import { convertToUserTimezone, formatDateAndTime, getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";
import { Overlay, Tooltip } from "react-bootstrap";
import { Info } from "../../../svgFiles/Info";

const Rework = () => {
  const { _t } = useTranslator();
  const [page] = useState(1);
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [activeTab, setActiveTab] = useState("cheeks");
  const [addEmployeeData, setAddEmployeeData] = useState(false);
  const [editReworkData, setEditReworkData] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("");
  const [tab] = useState("rework");
  const [humidity, setHumidity] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(date));

  const navigate = useNavigate();

  const { data: employeeData, isLoading: isEmployeeLoading, isFetching: isEmployeeFetching } = useGetReworkEmployeeDataQuery({ date , lot_number:lotNumber});
  const { data: cheeksData, isLoading: isCheeksLoading, isFetching: isCheeksFetching } = useGetReworkLogsQuery({ type: "cheeks", date , lot_number:lotNumber});
  const { data: stripsData, isLoading: isStripsLoading, isFetching: isStripsFetching } = useGetReworkLogsQuery({ type: "strips", date , lot_number:lotNumber});
  const { data: summaryData } = useGetReworkAreaSummaryQuery({ tab, date,lot_number:lotNumber });

  const [activeTooltipId, setActiveTooltipId] = useState(null);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const tooltipRefs = useRef({});

  const MULTISELECT_KEYS = ["seconds", "humidity", "green", "bone_seed", "toasted", "strange_material", "spot", "tapita", "pieces"];
  const editableTypes = ["seconds", "bone_seed", "strange_material", "spot", "tapita", "pieces"];

  const getOtherBreakdown = (row) =>
    MULTISELECT_KEYS.filter((k) => Number(row[k]) > 0).map((k) => ({ key: k, value: row[k] }));

  useEffect(() => {
    setLotNumber(makePriorLotNumber(date));
  }, [date]);

  const handleAddEmployee = () => {
    setAddEmployeeData(true);
    setModalMode("add");
    setSelectedRecord(null);
    setShowModal(true);
  };

  const handleEditReworkLog = (record) => {
    setEditReworkData(true);
    setModalMode("edit");
    setSelectedRecord(record);
    setShowModal(true);
  };

  const handleStartProcessing = (data) => {    
    if (data.entity_type === "green") navigate("/green-room");
    if (data.entity_type === "humidity") navigate("/humid");
    if (data.entity_type === "toasted") navigate("/toasted");
  };

  const handleTabClick = (tabName) => setActiveTab(tabName);

  const listItems = [
    { label: _t("total_prime_kg", "Total Prime KG"), value: summaryData?.data.total_no_of_prime_kg, icon: Box },
    { label: _t("prime_cheeks_kg", "Prime Cheeks KG"), value: summaryData?.data.total_no_of_prime_kg_in_cheeks, icon: KgGreen },
    { label: _t("prime_strips_kg", "Prime Strips KG"), value: summaryData?.data.total_no_of_prime_kg_in_strips, icon: KgGreen },
    { label: _t("rejected_kg", "Rejected KG"), value: summaryData?.data.total_no_of_trash_kg, icon: KgRejected },

    { label: _t("seconds_cheeks_kg", "Second Cheeks KG"), value: summaryData?.data.seconds_cheeks_kg, icon: KgGreen },
    { label: _t("seconds_strips_kg", "Second Strips KG"), value: summaryData?.data.seconds_strips_kg, icon: KgGreen },
    { label: _t("total_tapita_kg", "Total Tapita KG"), value: summaryData?.data.total_tapita_kg, icon: KgGreen },
    { label: _t("total_pieces_kg", "Total Pieces KG"), value: summaryData?.data.total_pieces_kg, icon: KgGreen },
  ];

  const getType = (type) => {
    switch (type) {
      case "seconds": return _t("seconds_kg", "Seconds KG");
      case "humidity": return _t("humidity_kg", "Humidity Kg");
      case "green": return _t("green_kg", "Green Kg");
      case "bone_seed": return _t("bone_seed_kg", "Bone/Seed Kg");
      case "toasted": return _t("toasted", "Toasted");
      case "strange_material": return _t("strange_material", "Strange Material");
      case "spot": return _t("spot", "Spot");
      case "tapita": return _t("tapita", "Tapita");
      case "pieces": return _t("pieces", "Pieces");
      default: return _t("spot", "Spot");
    }
  };

  const reworkColumns = [
    { label: _t("date", "Date"), accessor: "created_at", cell: ({ value }) => convertToUserTimezone(value) },
    { label: _t("batch_code", "Batch Code"), accessor: "lot_number" },
    { label: _t("types", "Types"), accessor: "entity_type", cell: ({ value }) => getType(value) },
    { label: _t("total_kg", "Total KG"), accessor: "total_kg" },
    { label: _t("kg_in_prime", "KG in Prime"), accessor: "prime", cell: ({ value }) => value },
    { label: _t("total_rejected", "Total Rejected"), accessor: "trash", cell: ({ value }) => value },
    { label: _t("other", "Other"), accessor: "other", cell: ({ row }) => {
      const val = row.other;
      if (!val || val === 0) return _t("no_data_yet", "No Data yet");
      const breakdown = getOtherBreakdown(row);
      if (!tooltipRefs.current[row.id]) tooltipRefs.current[row.id] = { trigger: { current: null } };
      return (
        <span className="d-flex align-items-center gap-1">
          {val}
          <span
            ref={(el) => { if (!tooltipRefs.current[row.id]) tooltipRefs.current[row.id] = {}; tooltipRefs.current[row.id].trigger = { current: el }; }}
            className="cursor-pointer"
            onMouseEnter={() => setActiveTooltipId(row.id)}
            onMouseLeave={() => { if (!isHoveringTooltip) setActiveTooltipId(null); }}
            onClick={() => setActiveTooltipId(activeTooltipId === row.id ? null : row.id)}
          >
            {Info}
          </span>
          <Overlay
            target={tooltipRefs.current[row.id]?.trigger?.current}
            show={activeTooltipId === row.id}
            placement="bottom"
          >
            {(props) => (
              <Tooltip
                id={`tooltip-other-${row.id}`}
                {...props}
                className="custom-tooltip"
                onMouseEnter={() => setIsHoveringTooltip(true)}
                onMouseLeave={() => { setIsHoveringTooltip(false); setActiveTooltipId(null); }}
              >
                <div className="tooltip-content">
                  <div className="tooltip-header">{_t("other_breakdown", "Other Breakdown")}</div>
                  <ul className="tooltip-list">
                    {breakdown.map((item) => (
                      <li key={item.key}>
                        <span>• {getType(item.key)}</span>
                        <span className="tooltip-amount">{item.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Tooltip>
            )}
          </Overlay>
        </span>
      );
    }},
    { label: _t("extra_strips", "Extra Strips"), accessor: "strips", cell: ({ value }) => value === 0 ? _t("no_data_yet", "No Data yet") : value },
    {
      label: _t("edit_processing", "Edit/Processing"),
      accessor: "entity_type",
      cell: ({ row }) => {
        if (row.entity_type === "green") {
          return <Button size="small" label={_t("view_data", "View Data")} onClick={() => navigate("/green-room")} />;
        }
        return editableTypes.includes(row.entity_type) ? (
          <Button
            size="small"
            label={row.status === "moved" ? _t("moved", "Moved") : _t("edit", "Edit")}
            onClick={() => handleEditReworkLog(row)}
            className="borderRedBtn"
            style={row.status === "moved" ? { opacity: 0.45 } : {}}
            disabled={row.status === "moved"}
          />
        ) : (
          <Button size="small" label={_t("start_processing", "Start Processing")} onClick={() => handleStartProcessing(row)} />
        );
      },
    },
  ];

  const employeeColumns = [
    { label: _t("employees_name", "Employees Name"), accessor: "employee_name" },
    { label: _t("kg_before_lunch", "Kg Before Lunch"), accessor: "kg_before_lunch" },
    { label: _t("kg_after_lunch", "Kg After Lunch"), accessor: "kg_after_lunch" },
    { label: _t("boxes_before_lunch", "Boxes Before Lunch"), accessor: "boxes_before_lunch" },
    { label: _t("boxes_after_lunch", "Boxes After Lunch"), accessor: "boxes_after_lunch" },
    { label: _t("total_kg", "Total KG"), accessor: "total_kg" },
    { label: _t("total_boxes", "Total Boxes"), accessor: "total_boxes" },
  ];  

  return humidity ? (
    <Humidity data={humidity} setShow={setHumidity} date={date} />
  ) : (
    <div>
      {addEmployeeData && (
        <AddEmployeeModal show={showModal} setShow={setAddEmployeeData} mode={modalMode} record={selectedRecord} lotNumber={lotNumber} />
      )}
      {editReworkData && (
        <EditReworkLogs show={showModal} setShow={setEditReworkData} mode={modalMode} record={selectedRecord} activeTab={activeTab} />
      )}

      <div className="overview cmn_card">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">{_t("rework_overview", "Rework Overview")}</h6>
          <div className="d-flex gap-2 align-items-center">
            <h6 className="mb-0">{_t("batch_code", "Batch Code")}</h6>
            <Button label={`#${lotNumber ?? 0}`} size="small" className="borderRedBtn" />
            <DateSelector setDate={setDate} />
          </div>
        </div>
        <ul className="m-0 grid_card_wrapper">
          {listItems.map((list, index) => (
            <GridCard
              key={index}
              title={list.label}
              icon={list.icon}
              value={list.value}
              day={currentDate === date ? _t("today", "Today") : formatDateAndTime(date, PROJECT_DATE_FORMAT)}
            />
          ))}
          
        </ul>
      </div>

      <div className="my-3">
        <Datatable
          title={`${_t("rework_logs", "Rework logs")} - (${_t('lot_no', 'Lot No.')} #${lotNumber})`}
          columns={reworkColumns}
          page={page}
          tableData={activeTab === "cheeks" ? cheeksData?.data : stripsData?.data}
          showCount={false}
          showPegination={false}
          tab={true}
          activeTab={activeTab}
          onTabChange={handleTabClick}
          isLoading={(activeTab === "cheeks" && (cheeksData?.isLoading || cheeksData?.isFetching)) || (activeTab === "strips" && (stripsData?.isLoading || stripsData?.isFetching))}
        />
      </div>

      <div className="my-3">
        <Datatable
          title={`${_t("employee_logs", "Employee logs")} - (${_t('lot_no', 'Lot No.')} #${lotNumber})`}
          columns={employeeColumns}
          tableData={employeeData?.data}
          page={page}
          buttons={[{ label: _t("add_employee", "Add Employee"), icon: PlusSvg, onClick: handleAddEmployee }]}
          showCount={false}
          showPegination={false}
          isLoading={employeeData?.isLoading || employeeData?.isFetching}
        />
      </div>
    </div>
  );
};

export default Rework;
